/**
 * SPA entry (ARCHITECTURE.md §8).
 *
 * Wires the providers the app needs — Query (server state), i18n (active locale),
 * Router (navigation) — plus the theme, then mounts into `#root`. The
 * `onUnauthorized` handler is injected here (not in `query.ts`) so a lost session
 * anywhere redirects to `/login` without a `query ⇄ router` import cycle.
 *
 * Locale is controlled state held at the root: `I18nProvider` receives the current
 * `locale`, and `onLocaleChange` persists (localStorage + `<html lang>`) then
 * re-renders. The language switcher in Settings drives this via `useI18n`.
 */
import { StrictMode, useCallback, useState } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nProvider } from "@richrich/i18n/react";
import type { Locale } from "@richrich/i18n";
import "./styles.css";
import { router } from "./router";
import { queryClient, setUnauthorizedHandler } from "./lib/query";
import { initTheme } from "./lib/theme";
import { initLocale, persistLocale, storedLocale } from "./lib/i18n";

initTheme();
initLocale();
setUnauthorizedHandler(() => {
  void router.navigate({ to: "/login" });
});

function App() {
  const [locale, setLocale] = useState<Locale>(() => storedLocale());
  const handleLocaleChange = useCallback((next: Locale) => {
    persistLocale(next);
    setLocale(next);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider locale={locale} onLocaleChange={handleLocaleChange}>
        <RouterProvider router={router} />
      </I18nProvider>
    </QueryClientProvider>
  );
}

const rootEl = document.getElementById("root");
if (rootEl === null) {
  throw new Error("Root element #root not found");
}

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
